import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { calendarApi } from './api';
import CalendarView from './CalendarView';
import CalendarTasks from './CalendarTasks';
import CalendarSideBySide from './CalendarSideBySide';
import { CalendarDayDrill, CalendarQuad, CalendarYear } from './CalendarMulti';
import {
  DEFAULT_VIEW, EMPTY_LEGEND, LAYOUTS, LAYOUT_ICON, SHOW_ALL_KEY, TEXT_SIZES, TEXT_SIZE_KEY, calendarColors, legendDefaultIds,
  legendShown, optionLabel, paneColors, readDisplayPrefs, textSizePx, toggleLegend, writeDisplayPref,
} from './calendarPanes';
import TodayPanel from './TodayPanel';
import CalendarImport, { isIcsFile } from './CalendarImport';
import HsLockStrip from './HsLockStrip';   // 5.44.0 "HealthSherpa in use" (every page)

// =============================================================================
// CalendarApp (5.32.0) — the Calendar + Tasks page as its OWN browser tab (App renders it instead of Main when the
// address has ?calendar=1, after the same login), like HealthSherpaApp. Calendars live on HDCal (Davis CalDAV);
// the server decides what this login may see (services/calendar/access.py):
//   - everyone: the Office calendar; a calendar-enabled login also its own Personal / Tasks and calendars shared
//     to it; logins without a personal calendar see Office only;
//   - ADMINS ONLY: a person picker to view / edit another calendar-enabled user's calendar and tasks, and a note
//     listing calendar-enabled users who have no HDCal account yet (accounts are made by hand in Davis).
// 5.33.0: two layouts — "Side by side" (default; CalendarSideBySide: two panes, one calendar each, one shared
// toolbar) and "Overlay" (the 5.32 combined view + legend, unchanged). Layout and the left / right pane choices are
// saved per login on the server (user_settings via /api/calendar/view-prefs). Opens in Month; switching layouts keeps
// the view type and dates. The admin person picker drives Overlay and Tasks; in Side by side the pane dropdowns do.
// 5.36.0: side-by-side panes are independent unless 🔗 linked (link / pane views / month offset saved per login with
// the other cal.* keys); text size (Small / Medium / Large / Extra large, default Medium — scales the calendar views
// and the Tasks tab) and "Show all events" (default on) are remembered PER COMPUTER in this browser's storage.
// 5.37.0: layouts Overlay (DEFAULT) | Side by side | Quad | Year. Overlay opens with ONLY the viewed person's own
// Personal ticked (Office when they have none); the legend ticks are saved per login on the server (cal.legend,
// replacing the 5.32 browser key insuredocs.calendar.hidden.v1, which is no longer read). Quad / Year show one
// calendar (cal.quad / cal.year) over 4 / 12 months; clicking a day opens its Day view with Back (CalendarMulti.jsx).
// 5.37.1: the Overlay legend of the login's own view lists every calendar the server allows (GET /panes → legend:
// order + default ticks by login type; admins also see people's Personal, fetched through ?user= like a person pane);
// calendar colours come from HDCal (calendar.color) with a green / purple-free fallback (calendarPanes.js).
// =============================================================================

// 5.43.0: "📥 Import .ics" on the toolbar (CalendarImport.jsx); dropping an .ics anywhere on this page opens the same
// window with that file loaded.
export const CALENDAR_WINDOW_NAME = 'insuredocs-calendar';
const C = { bg: '#1a1a1a', panel: '#242424', border: '#333', text: '#f0f0f0', dim: '#aaa', violet: '#7c3aed' };
export default function CalendarApp() {
  // 5.34.0: the Today panel opens this tab as /?calendar=1&day=YYYY-MM-DD&event=<id> or &tab=tasks
  const [start] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const day = /^\d{4}-\d{2}-\d{2}$/.test(q.get('day') || '') ? q.get('day') : null;
      const ev = Number(q.get('event')) || null;
      // 5.38.0 &user=<username>: a client follow-up on someone else's calendar (admins; the server refuses others)
      const who = /^[A-Za-z0-9._-]{1,50}$/.test(q.get('user') || '') ? q.get('user') : null;
      if (q.get('day') || q.get('event') || q.get('tab') || q.get('user')) window.history.replaceState(null, '', '/?calendar=1');
      return { day, ev, tasks: q.get('tab') === 'tasks', user: who };
    } catch (_e) { return { day: null, ev: null, tasks: false, user: null }; }
  });
  const [tab, setTab] = useState(start.tasks ? 'tasks' : 'calendar');
  const [focus, setFocus] = useState(start.ev ? { objectId: start.ev, n: 1 } : { objectId: null, n: 0 });
  const [todayMode, setTodayMode] = useState('none');
  const [target, setTarget] = useState(start.user);     // null = me; admins may pick a username (5.38.0: &user=)
  const [ov, setOv] = useState(null);
  const [err, setErr] = useState('');
  const [legend, setLegend] = useState(EMPTY_LEGEND);    // 5.37.0 Overlay ticks over the default (server-saved)
  const [quadKey, setQuadKey] = useState('');            // 5.37.0 Quad / Year calendar (pane option key)
  const [yearKey, setYearKey] = useState('');
  const [drill, setDrill] = useState(null);              // 5.37.0 {date, from: 'quad'|'year'} = a day opened from Quad / Year
  const [reloadKey, setReloadKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [panes, setPanes] = useState(null);             // {options, prefs, defaults} from /api/calendar/panes
  const [layout, setLayout] = useState(null);           // 'overlay' | 'side' | 'quad' | 'year' (null while loading)
  const [left, setLeft] = useState('');
  const [right, setRight] = useState('');
  const [nav, setNav] = useState({ view: DEFAULT_VIEW, date: start.day ? new Date(`${start.day}T00:00:00Z`) : null });   // shared by both layouts
  const [paneReload, setPaneReload] = useState(0);
  const [prefErr, setPrefErr] = useState('');
  const [sbs, setSbs] = useState({ link: false, left_view: DEFAULT_VIEW, right_view: DEFAULT_VIEW, offset: 0 });   // 5.36.0
  const [display, setDisplay] = useState(readDisplayPrefs);       // 5.36.0 per computer: {size, showAll}
  const [importer, setImporter] = useState(null);                  // 5.43.0 {file} while the import window is open
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Calendar — InsureDocs';
    try { if (!window.name) window.name = CALENDAR_WINDOW_NAME; } catch (_e) { /* ignore */ }
    const prevBg = document.body.style.background;
    document.body.style.background = C.bg;
    return () => { document.title = prev; document.body.style.background = prevBg; };
  }, []);

  const loadOverview = useCallback(async () => {
    try { const r = await calendarApi.overview(target); setOv(r.data); setErr(''); } catch (e) { setErr(e?.response?.data?.detail || 'Could not load the calendar'); }
  }, [target]);
  useEffect(() => { loadOverview(); }, [loadOverview]);

  useEffect(() => {
    calendarApi.panes().then((r) => {
      const p = r.data.prefs;
      // 5.38.0 opening one item (event / another person's calendar) always shows it in Overlay — not saved
      const forceOverlay = start.user || (start.ev && (p.layout === 'quad' || p.layout === 'year'));
      setPanes(r.data); setLayout(forceOverlay ? 'overlay' : p.layout); setLeft(p.left); setRight(p.right);
      setSbs({ link: !!p.link, left_view: p.left_view || DEFAULT_VIEW, right_view: p.right_view || DEFAULT_VIEW, offset: Number(p.offset) || 0 });
      if (p.layout === 'side' && p.left_view) setNav((n) => ({ ...n, view: p.left_view }));   // left pane reopens in its view
      setLegend(p.legend || EMPTY_LEGEND); setQuadKey(p.quad || ''); setYearKey(p.year || '');
    }).catch((e) => {
      setPanes({ options: [] }); setLayout('overlay');          // the 5.32 view still works without pane options
      setPrefErr(e?.response?.data?.detail || 'Could not load the side-by-side choices — showing Overlay');
    });
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps -- `start` is read once per page

  const savePrefs = (p) => {
    calendarApi.savePrefs(p).then(() => setPrefErr('')).catch((e) => setPrefErr(e?.response?.data?.detail || 'Could not save this choice'));
  };
  // 5.36.0 pane navigation prefs: kept locally at once, saved to the server shortly after (prev / next clicks in a
  // row become one save)
  const pending = useRef({});
  const timer = useRef(null);
  const flushNav = useCallback(() => {
    clearTimeout(timer.current); timer.current = null;
    const body = pending.current;
    pending.current = {};
    if (Object.keys(body).length) savePrefs(body);
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const queueSave = useCallback((p) => {
    pending.current = { ...pending.current, ...p };
    clearTimeout(timer.current);
    timer.current = setTimeout(flushNav, 400);
  }, [flushNav]);
  const onNavPrefs = useCallback((p) => { setSbs((s) => ({ ...s, ...p })); queueSave(p); }, [queueSave]);
  useEffect(() => () => flushNav(), [flushNav]);
  const onLink = useCallback((on) => {
    // linking lines the right pane up with the left one (same view, offset 0); unlinking starts from there too
    onNavPrefs(on ? { link: true, offset: 0, right_view: sbs.left_view } : { link: false, offset: 0 });
  }, [onNavPrefs, sbs.left_view]);
  const setSize = (size) => { setDisplay((d) => ({ ...d, size })); writeDisplayPref(TEXT_SIZE_KEY, size); };
  const setShowAll = (on) => { setDisplay((d) => ({ ...d, showAll: on })); writeDisplayPref(SHOW_ALL_KEY, on ? '1' : '0'); };
  const fontPx = textSizePx(display.size);

  const chooseLayout = (l) => { setDrill(null); if (l === layout) return; setLayout(l); savePrefs({ layout: l }); };
  const pickOne = (which, key) => {                      // Quad / Year calendar
    if (which === 'quad') setQuadKey(key); else setYearKey(key);
    savePrefs({ [which]: key });
  };
  const pick = (slot, key) => {
    if (slot === 'left') setLeft(key); else setRight(key);
    savePrefs({ [slot]: key });
  };
  const onNav = useCallback((n) => setNav(n), []);
  const bumpPanes = useCallback(() => setPaneReload((k) => k + 1), []);
  const side = tab === 'calendar' && layout === 'side';
  const multi = tab === 'calendar' && (layout === 'quad' || layout === 'year');
  const overlay = tab === 'calendar' && layout === 'overlay';

  // 5.34.0 Today panel (docked right column here). Opening an event: own view, that day, the event's dialog — the
  // views remount (key = focus.n) so the new date applies. Side by side needs a pane in the login's own view.
  const onTodayLayout = useCallback((m) => {
    setTodayMode(m);
    setTimeout(() => { try { window.dispatchEvent(new Event('resize')); } catch (_e) { /* ignore */ } }, 50);
  }, []);
  const openFromToday = (day, objectId) => {
    setTab('calendar'); setTarget(null); setDrill(null);
    if (layout === 'quad' || layout === 'year') setLayout('overlay');   // 5.37.0: the event opens in Overlay (not saved)
    setNav((n) => ({ view: n.view, date: new Date(`${day}T00:00:00Z`) }));
    if (layout === 'side' && panes?.options) {
      const own = (k) => (panes.options.find((o) => o.key === k) || {}).user === null;
      if (!own(left) && !own(right)) setRight(panes.options.some((o) => o.key === 'mine') ? 'mine' : 'office');   // not saved
    }
    setFocus((f) => ({ objectId, n: f.n + 1 }));
  };
  const openTasksFromToday = () => { setTarget(null); setTab('tasks'); };

  const toggle = (id) => {
    const next = toggleLegend(legend, id, legendDefaults);
    setLegend(next);
    queueSave({ legend: next });
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      if (overlay && !target) {      // 5.37.1: me + the people ticked in the legend
        const users = [null, ...new Set(legendItems.filter((i) => i.user && !hidden.has(i.id)).map((i) => i.user))];
        for (const u of users) await calendarApi.refresh(u);   // eslint-disable-line no-await-in-loop
      } else if (side || multi) {    // the people shown in the panes / Quad / Year (null = me)
        const shownKeys = side ? [left, right] : [layout === 'quad' ? quadKey : yearKey];
        const users = [...new Set((panes?.options || []).filter((o) => shownKeys.includes(o.key)).map((o) => o.user || null))];
        if (!users.includes(null)) users.unshift(null);
        for (const u of users) await calendarApi.refresh(u);   // eslint-disable-line no-await-in-loop
      } else {
        await calendarApi.refresh(target);
      }
    } catch (e) { window.alert(e?.response?.data?.detail || 'Refresh failed'); }
    finally { setRefreshing(false); setReloadKey((k) => k + 1); setPaneReload((k) => k + 1); loadOverview(); }
  };

  const close = () => {
    try { window.close(); } catch (_e) { /* not script-closable */ }
    setTimeout(() => { if (!window.closed) window.location.href = '/'; }, 300);
  };

  const calendars = useMemo(() => (ov ? ov.calendars.filter((c) => c.components.includes('VEVENT')) : []), [ov]);
  const optColors = useMemo(() => paneColors(panes?.options || []), [panes]);
  // 5.37.1 legend: the login's own view = the server's list (order + default ticks); another person's view (admin
  // picker) = that view's calendars with their Personal + Office ticked by default. Tasks never.
  const ownLegend = !target && !!panes?.legend?.items?.length;
  const legendItems = useMemo(() => {
    if (ownLegend) {
      const byKey = Object.fromEntries((panes.options || []).map((o) => [o.key, o]));
      return panes.legend.items.map((k) => byKey[k]).filter(Boolean).map((o) => ({
        id: o.calendar.id, key: o.key, name: optionLabel(o), user: o.user, kind: o.calendar.kind,
        writable: o.calendar.writable, color: optColors[o.key], cal: o.calendar }));
    }
    const cc = calendarColors(calendars);
    return calendars.filter((c) => c.kind !== 'tasks').map((c) => ({ id: c.id, key: `id:${c.id}`, name: c.name, user: null, kind: c.kind,
      writable: c.writable, color: cc[c.id], cal: c }));
  }, [ownLegend, panes, optColors, calendars]);
  const legendDefaults = useMemo(() => (ownLegend ? panes.legend.default_on : legendDefaultIds(calendars)), [ownLegend, panes, calendars]);
  const hidden = useMemo(() => new Set(legendItems.filter((i) => !legendShown(legend, i.id, legendDefaults)).map((i) => i.id)), [legendItems, legend, legendDefaults]);
  const colors = useMemo(() => Object.fromEntries(legendItems.map((i) => [i.id, i.color])), [legendItems]);
  const extraSources = useMemo(() => legendItems.filter((i) => i.user && !hidden.has(i.id)).map((i) => ({ user: i.user, calendarId: i.id })), [legendItems, hidden]);
  const overlayOv = useMemo(() => (ov ? { ...ov, calendars: [...ov.calendars, ...legendItems.filter((i) => i.user).map((i) => ({ ...i.cal, user: i.user }))] } : ov), [ov, legendItems]);
  const multiKey = layout === 'quad' ? quadKey : yearKey;
  const multiOpt = (panes?.options || []).find((o) => o.key === multiKey) || (panes?.options || [])[0];
  const isAdmin = !!ov?.me?.is_admin;
  const tgt = ov?.target;
  const viewingOther = !!target;

  const btn = (active) => ({ padding: '7px 16px', border: 'none', borderRadius: 6, background: active ? C.violet : '#2f2f2f', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' });

  const hasFiles = (e) => !!e.dataTransfer && [...(e.dataTransfer.types || [])].includes('Files');
  const pageDrop = {
    onDragOver: (e) => { if (hasFiles(e) && !importer) { e.preventDefault(); setDragOver(true); } },
    onDragLeave: (e) => { if (e.currentTarget === e.target) setDragOver(false); },
    onDrop: (e) => {
      if (!hasFiles(e) || importer) return;
      e.preventDefault(); setDragOver(false);
      const f = e.dataTransfer.files[0];
      if (f) setImporter({ file: f, ics: isIcsFile(f) });
    },
  };
  return (
    <div style={{ height: '100vh', display: 'grid', gridTemplateRows: 'auto auto 1fr', background: C.bg, color: C.text }} {...pageDrop} data-testid="cal-page">
      <div data-testid="hslock-slot"><HsLockStrip /></div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: '8px 16px', background: C.panel, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: '#a78bfa', whiteSpace: 'nowrap' }}>📅 Calendar
          <span style={{ fontSize: 11, fontWeight: 400, color: '#888', marginLeft: 8 }}>InsureDocs v{process.env.REACT_APP_VERSION || 'UNSET'}</span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" style={btn(tab === 'calendar')} onClick={() => setTab('calendar')} data-testid="cal-tab-calendar">Calendar</button>
          <button type="button" style={btn(tab === 'tasks')} onClick={() => setTab('tasks')} data-testid="cal-tab-tasks">✔ Tasks</button>
        </div>
        {tab === 'calendar' && layout && panes?.options?.length > 0 && (
          <div style={{ display: 'flex', gap: 0, border: `1px solid ${C.border}`, borderRadius: 7, overflow: 'hidden' }} title="Layout (remembered for your login)">
            {LAYOUTS.map(([l, label]) => (
              <button key={l} type="button" onClick={() => chooseLayout(l)} data-testid={`cal-layout-${l}`}
                style={{ padding: '6px 12px', border: 'none', background: layout === l ? '#0e7490' : '#2f2f2f', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {LAYOUT_ICON[l]} {label}
              </button>
            ))}
          </div>
        )}
        {isAdmin && ov.people && (overlay || tab === 'tasks') && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.dim }}>
            Viewing
            <select value={target || ''} onChange={(e) => { setTarget(e.target.value || null); }} data-testid="cal-person-picker"
              style={{ padding: '6px 8px', background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13 }}>
              <option value="">Me ({ov.me.full_name})</option>
              {/* 5.32.1: one row per InsureDocs user, by display name (a calendar account belongs to one user only);
                  the username is added only when two people share a display name */}
              {ov.people.filter((p) => p.username !== ov.me.username).map((p, _i, list) => (
                <option key={p.username} value={p.username}>
                  {p.full_name}{list.filter((q) => q.full_name === p.full_name).length > 1 ? ` (${p.username})` : ''}{p.has_account === false ? ' — no calendar account' : ''}
                </option>
              ))}
            </select>
          </label>
        )}
        {!multi && <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: C.dim }} title="Text size on this computer (not tied to your login)">
          Aa
          <select value={display.size} onChange={(e) => setSize(e.target.value)} data-testid="cal-text-size"
            style={{ padding: '5px 6px', background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 }}>
            {TEXT_SIZES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </label>}
        {tab === 'calendar' && !multi && (
          <button type="button" onClick={() => setShowAll(!display.showAll)} data-testid="cal-show-all" aria-pressed={display.showAll}
            title={display.showAll ? 'Month days grow to show every event. Click for compact days with "+N more".' : 'Compact days with "+N more". Click to show every event.'}
            style={{ padding: '6px 10px', border: `1px solid ${display.showAll ? '#0e7490' : C.border}`, borderRadius: 6, background: display.showAll ? '#0e7490' : '#2f2f2f', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {display.showAll ? '☰ Show all events' : '☰ Compact (+more)'}
          </button>
        )}
        {viewingOther && tgt && (overlay || tab === 'tasks') && <span style={{ fontSize: 12, background: '#78350f', color: '#fde68a', padding: '3px 8px', borderRadius: 5 }}>Viewing {tgt.full_name}'s calendar</span>}
        <div style={{ flex: 1 }} />
        <button type="button" onClick={() => setImporter({ file: null })} data-testid="cal-import"
          title="Add events from an .ics file (Google, Outlook, iPhone exports) — or drop the file on this page"
          style={btn(false)}>📥 Import .ics</button>
        <button type="button" onClick={refresh} disabled={refreshing} title="Fetch the latest from HDCal now (it also refreshes by itself every minute)"
          style={{ ...btn(false), opacity: refreshing ? 0.6 : 1 }}>{refreshing ? '⟳ Refreshing…' : '⟳ Refresh'}</button>
        <button type="button" onClick={close} style={{ ...btn(false), background: '#444' }}>Close</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: `${tab === 'calendar' && layout === 'overlay' ? '220px ' : ''}minmax(0, 1fr)${todayMode !== 'none' ? ' auto' : ''}`, minHeight: 0 }}>
        {tab === 'calendar' && layout === 'overlay' && (
          <div style={{ borderRight: `1px solid ${C.border}`, padding: 14, overflowY: 'auto', background: C.panel }}>
            <div style={{ fontSize: 11, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Calendars</div>
            <div data-testid="cal-legend">
              {legendItems.map((c) => (
                <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 7, cursor: 'pointer' }} title={c.name}>
                  <input type="checkbox" checked={!hidden.has(c.id)} onChange={() => toggle(c.id)} data-testid={`cal-legend-${c.id}`} data-kind={c.kind} data-key={c.key} />
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: c.color, flexShrink: 0 }} data-testid={`cal-legend-color-${c.id}`} />
                  <span style={{ wordBreak: 'break-word', lineHeight: 1.25 }}>{c.name}{!c.writable ? ' (read-only)' : ''}</span>
                </label>
              ))}
            </div>
            {ov && legendItems.length === 0 && <div style={{ fontSize: 12, color: C.dim }}>No calendars yet — they appear within a minute of the first sync.</div>}
            {tgt && !tgt.enabled && <div style={{ fontSize: 12, color: C.dim, marginTop: 10 }}>This login has no personal calendar — Office only.</div>}
            {tgt && tgt.enabled && tgt.has_account === false && (
              <div style={{ fontSize: 12, color: '#fbbf24', marginTop: 10 }}>No calendar account yet{viewingOther ? ' for this person' : ''} — Office only until one is set up.</div>
            )}
            {isAdmin && ov.missing && ov.missing.length > 0 && (
              <div data-testid="cal-missing" style={{ marginTop: 18, fontSize: 12, color: C.dim, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
                <div style={{ fontWeight: 700, color: '#fbbf24', marginBottom: 4 }}>Missing a calendar account</div>
                {ov.missing.map((m) => <div key={m.username}>{m.full_name} ({m.account || m.username})</div>)}
                <div style={{ marginTop: 6 }}>Create them in the Davis dashboard (the name in brackets); Personal + Tasks are added automatically.</div>
              </div>
            )}
            <div style={{ fontSize: 11, color: '#777', marginTop: 18 }}>Ticks are remembered for your login. All times are Central (Chicago).</div>
          </div>
        )}
        <div style={{ padding: tab === 'calendar' ? '8px 12px' : 16, minHeight: 0, overflow: tab === 'tasks' ? 'auto' : 'hidden', '--idcal-fs': `${fontPx}px` }} data-testid="cal-content" data-text-size={display.size}>
          {err && <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '8px 10px', borderRadius: 6, fontSize: 13, marginBottom: 10 }}>{err}</div>}
          {prefErr && tab === 'calendar' && <div style={{ background: '#78350f', color: '#fde68a', padding: '6px 10px', borderRadius: 6, fontSize: 12, marginBottom: 8 }}>{prefErr}</div>}
          {ov && tab === 'calendar' && !layout && <div style={{ color: C.dim, fontSize: 13 }}>Loading…</div>}
          {ov && tab === 'calendar' && layout === 'overlay' && (
            <CalendarView key={`ov-${focus.n}`} target={target} overview={overlayOv} colors={colors} hidden={hidden} reloadKey={reloadKey} showAll={display.showAll}
              extraSources={target ? undefined : extraSources}
              openRequest={focus.objectId ? focus : null}
              initialView={nav.view} initialDate={nav.date} onDatesSet={(arg) => onNav({ view: arg.view.type, date: arg.view.calendar.getDate() })} />
          )}
          {ov && side && (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <CalendarSideBySide key={`sbs-${focus.n}`} options={panes.options} left={left} right={right} onPick={pick} me={ov.me}
                nav={nav} onNav={onNav} reloadKey={paneReload} onChanged={bumpPanes} openRequest={focus.objectId ? focus : null}
                link={sbs.link} onLink={onLink} paneViews={{ left: sbs.left_view, right: sbs.right_view }} offset={sbs.offset}
                onNavPrefs={onNavPrefs} showAll={display.showAll} />
              <div style={{ fontSize: 11, color: '#777', marginTop: 4 }}>All times are Central (Chicago).</div>
            </div>
          )}
          {ov && multi && multiOpt && (
            <div style={{ height: '100%', minHeight: 0 }}>
              {/* the grid stays mounted (hidden) while a day is open, so Back returns to the same months / year */}
              <div style={{ height: '100%', minHeight: 0, display: drill ? 'none' : 'block' }}>
                {layout === 'quad'
                  ? <CalendarQuad options={panes.options} value={multiOpt.key} onPick={(k) => pickOne('quad', k)} me={ov.me}
                    color={optColors[multiOpt.key]} reloadKey={paneReload} onOpenDay={(d) => setDrill({ date: d, from: 'quad' })} />
                  : <CalendarYear options={panes.options} value={multiOpt.key} onPick={(k) => pickOne('year', k)} me={ov.me}
                    color={optColors[multiOpt.key]} reloadKey={paneReload} onOpenDay={(d) => setDrill({ date: d, from: 'year' })} />}
              </div>
              {drill && (
                <CalendarDayDrill key={`${drill.from}:${drill.date}:${multiOpt.key}`} options={panes.options} value={multiOpt.key} me={ov.me}
                  color={optColors[multiOpt.key]} date={drill.date} from={drill.from} onBack={() => setDrill(null)}
                  reloadKey={paneReload} onChanged={bumpPanes} />
              )}
            </div>
          )}
          {ov && tab === 'tasks' && (
            <div style={{ zoom: fontPx / 13 }} data-testid="cal-tasks-zoom">
              <CalendarTasks key={target || 'me'} target={target} enabled={!!tgt?.enabled && tgt?.has_account !== false} />
            </div>
          )}
        </div>
        {/* 5.50.0: the red Today panel also carries the reminders on this page (the purple pop-up is gone here) */}
        <TodayPanel onLayout={onTodayLayout} onOpenEvent={openFromToday} onOpenTasks={openTasksFromToday} />
      </div>
      {dragOver && !importer && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 990, pointerEvents: 'none', border: '4px dashed #7c3aed', background: 'rgba(46,16,101,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, color: '#fff' }} data-testid="cal-drop-hint">Drop the .ics file to import it</div>
      )}
      {importer && (
        <CalendarImport initialFile={importer.file} onClose={() => setImporter(null)}
          onImported={() => { setReloadKey((k) => k + 1); setPaneReload((k) => k + 1); }} />
      )}
    </div>
  );
}
